import { consola } from 'consola'
import { watch } from 'vue'
import { initYmAdapter, isLocalHostname, resolveYmCounterId } from '@shared/lib/analytics'
import { useCookieConsent } from '@shared/lib'

const logger = consola.withTag('ym')

const TAG_SRC = 'https://mc.yandex.ru/metrika/tag.js'

/**
 * Загружает tag.js ровно один раз. Официальный сниппет Метрики делает то же самое:
 * сначала объявляет очередь `window.ym`, затем подтягивает скрипт, который эту
 * очередь разбирает. Раньше здесь была ТОЛЬКО очередь — скрипт не грузился никогда,
 * поэтому все вызовы копились в `window.ym.a` и счётчик не получал ни одного хита.
 */
const loadTag = (): void => {
  window.ym = window.ym || function (...args: unknown[]) {
    (window.ym.a = window.ym.a || []).push(args)
  }
  window.ym.l = Date.now()

  // Повторная вставка скрипта ломает Вебвизор — проверяем, не подключён ли он уже.
  const alreadyLoaded = [...document.scripts].some(script => script.src === TAG_SRC)
  if (alreadyLoaded) return

  const script = document.createElement('script')
  script.async = true
  script.src = TAG_SRC
  document.head.append(script)
}

export default defineNuxtPlugin((nuxtApp) => {
  const config = useRuntimeConfig()
  const isMobile = config.public.buildTarget === 'mobile'

  // Счётчик выбирается общим резолвером — им же пользуется адаптер целей, чтобы сайт
  // и приложение не разъехались по разным ID.
  const counterId = resolveYmCounterId({
    buildTarget: config.public.buildTarget as string | undefined,
    ymCounterId: config.public.ymCounterId as string | undefined,
    ymCounterIdMobile: config.public.ymCounterIdMobile as string | undefined,
  })

  // Пустой ID = метрика выключена. Так же гейтятся стенды: переменная задаётся только
  // в прод-профиле бренда. Хардкода ID здесь быть не должно — проект мультибрендовый,
  // общий счётчик смешал бы данные разных заведений.
  if (!counterId) {
    // В мобильной сборке при заданном счётчике сайта это почти наверняка забытая
    // переменная, а не осознанное отключение — молчать об этом нельзя: приложение
    // просто перестаёт считаться, и заметно это только по пустым отчётам.
    if (isMobile && config.public.ymCounterId) {
      logger.warn('Метрика приложения выключена: задан ymCounterId, но нет ymCounterIdMobile')
    } else {
      logger.debug(`Метрика выключена: не задан ${isMobile ? 'ymCounterIdMobile' : 'ymCounterId'}`)
    }
    return
  }

  // В dev метрику не поднимаем: боевой ID лежит в .env, и хиты с localhost засоряли бы
  // отчёты бренда. Для отладки целей — NUXT_PUBLIC_YM_DEV=1 (визиты уйдут в прод-счётчик).
  // Флаг читается из runtimeConfig: import.meta.env не видит переменные без префикса VITE_.
  const devOverride = config.public.ymDevEnabled === '1'

  if (import.meta.dev && !devOverride) {
    logger.debug('Метрика выключена в dev (включить: NUXT_PUBLIC_YM_DEV=1)')
    return
  }

  // Одного dev-флага мало: собранное приложение (`preview`, стенд на машине разработчика)
  // считает себя продом, и боевой счётчик из .env начал бы собирать чужие визиты.
  // Мобильная сборка сюда не попадает — WebView живёт на localhost БЕЗ порта.
  const { hostname, port } = window.location
  if (!devOverride && port && isLocalHostname(hostname)) {
    logger.warn(`Метрика выключена: страница открыта локально (${hostname}:${port})`)
    return
  }

  // Nuxt — SPA: смену маршрута Метрика сама не видит, хит на каждую страницу шлём руками.
  // Один и тот же URL подряд не отправляем: page:finish срабатывает и на первичной
  // гидрации, и после редиректов внутри одного маршрута.
  let lastHitUrl = ''
  const hit = (): void => {
    const url = window.location.href
    if (url === lastHitUrl) return
    lastHitUrl = url
    window.ym(counterId, 'hit', url)
  }

  let started = false

  const start = (): void => {
    if (started) return
    started = true

    loadTag()

    window.ym(counterId, 'init', {
      clickmap: true,          // карта кликов
      trackLinks: true,
      accurateTrackBounce: true,
      webvisor: true,          // Вебвизор + карта скроллов
      ecommerce: 'dataLayer',
      // Первый хит шлём сами (ниже), иначе он задвоится с хитом из page:finish.
      defer: true,
    })

    window.dataLayer = window.dataLayer || []

    // Адаптер целей узнаёт счётчик только отсюда: если метрика не поднялась (dev, пустой
    // ID, блокировщик) — reachGoal просто не вызывается, а события всё равно уедут в свой бэк.
    initYmAdapter(counterId)

    hit()
    nuxtApp.hook('page:finish', hit)

    logger.debug(`Метрика инициализирована: счётчик ${counterId}`)
  }

  // Гейт по согласию. Раньше счётчик поднимался безусловно, то есть аналитические cookie
  // и запись вебвизора начинались до того, как человек нажал хоть что-то в баннере.
  // Скрипт Метрики не подключаем вовсе, пока согласия нет: подключить его и «не считать»
  // нельзя — тег ставит свои cookie сам, в момент загрузки.
  const { analyticsAllowed } = useCookieConsent()

  // Отзыв согласия внутри уже загруженной вкладки счётчик не выключает: выгрузить тег
  // Метрики нечем. Поэтому единственный момент, когда решение имеет силу, — до старта;
  // после отказа страница живёт без Метрики до конца сессии, а после согласия счётчик
  // поднимается сразу, не дожидаясь перезагрузки.
  // Без `immediate: true`: при уже данном согласии колбэк отработал бы синхронно
  // внутри самого watch() — то есть до того, как переменная со «стопом» получит
  // значение, и обращение к ней упало бы на TDZ. Поэтому текущее состояние
  // проверяем отдельным вызовом уже после подписки.
  const handleConsent = (allowed: boolean): void => {
    if (!allowed) return
    start()
    stopWatching()
  }

  const stopWatching = watch(analyticsAllowed, handleConsent)

  // Текущее состояние проверяем здесь, после подписки: к этому моменту stopWatching
  // уже инициализирована, и колбэк может её вызвать.
  handleConsent(analyticsAllowed.value)

  if (!analyticsAllowed.value) {
    logger.debug('Метрика ждёт согласия на аналитические cookie')
  }
})
